import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toaster, { Theme } from '../../../../shared/toaster';
import {
  BULK_ACTION_TIMEOUT,
  MAX_POLLING_LIMIT,
  LeadFinderSecondaryTab,
} from '../../types';
import {
  INSUFFICIENT_CREDITS_ERROR_MESSAGE,
  getV2SavedTabPageSize,
  isInsufficientCreditsError,
} from '../../helpers/helper';
import {
  revealCompaniesAPI,
  getCompaniesV2,
  getSavedCompanies,
} from '../../helpers/leads.api';
import { enableBulkActionRequestPolling } from '../../../home/home-slice';
import { SystemErrors } from '../../../../shared/enums/system-errors';
import { getPostLoadMetaRequest } from '../../../home/extra-actions';
import { ExceptionTypes } from '../../../../shared/types/exception-types';

interface UseV2CompanyRevealProps {
  companiesData: {
    profiles: any[];
    pagination: any;
  };
  handleGeneratePayload: () => any;
  setCompaniesData: React.Dispatch<
    React.SetStateAction<{
      profiles: any[];
      pagination: any;
    }>
  >;
  setEnrichingCompanies: React.Dispatch<
    React.SetStateAction<Record<number, boolean>>
  >;
  enrichingCompanies: Record<number, boolean>;
  activeSubTab?: any;
  apiParam?: any;
  savedApiParam?: any;
  isLoading?: boolean;
}

export const useV2CompanyReveal = ({
  companiesData,
  handleGeneratePayload,
  setCompaniesData,
  setEnrichingCompanies,
  enrichingCompanies,
  activeSubTab,
  apiParam,
  savedApiParam,
  isLoading = false,
}: UseV2CompanyRevealProps) => {
  const dispatch = useDispatch();
  const shouldLogoutUser = useSelector(
    (state: any) => state.home.application.shouldLogoutUser,
  );
  const [isPollingEnabled, setIsPollingEnabled] = useState(false);
  const [isFirstPollRequest, setIsFirstPollRequest] = useState(true);
  const pollingAttemptsRef = useRef(0);
  const timerIdRef = useRef<NodeJS.Timeout | null>(null);
  const hasEnrichingCompaniesRef = useRef(false);
  const lastPollingResponseRef = useRef<any>(null);
  const companiesDataRef = useRef(companiesData);
  const backendSupportsIsRevealingRef = useRef(false);
  const previousSubTabRef = useRef(activeSubTab);
  const pollingPageRef = useRef<number | null>(null);
  const pollingFiltersRef = useRef<string | null>(null);
  const isStartingPollingRef = useRef(false);
  const processedCombinationsRef = useRef<Set<string>>(new Set());
  const previousIsLoadingRef = useRef<boolean>(false);

  const stopSearchPolling = useCallback(
    (shouldDispatchPostLoadMeta = true) => {
      setIsPollingEnabled(false);
      setEnrichingCompanies({});
      hasEnrichingCompaniesRef.current = false;
      pollingAttemptsRef.current = 0;
      backendSupportsIsRevealingRef.current = false;

      if (timerIdRef.current) {
        clearInterval(timerIdRef.current);
        timerIdRef.current = null;
      }

      pollingPageRef.current = null;
      pollingFiltersRef.current = null;
      lastPollingResponseRef.current = null;
      isStartingPollingRef.current = false;
      setIsFirstPollRequest(true);

      if (shouldDispatchPostLoadMeta) {
        dispatch(getPostLoadMetaRequest());
      }
    },
    [dispatch, setEnrichingCompanies],
  );

  // Keep ref in sync with companiesData to avoid stale closures
  useEffect(() => {
    companiesDataRef.current = companiesData;
  }, [companiesData]);

  useEffect(() => {
    const previousTab = previousSubTabRef.current;
    previousSubTabRef.current = activeSubTab;

    if (previousTab && previousTab !== activeSubTab && isPollingEnabled) {
      setIsPollingEnabled(false);
      setEnrichingCompanies({});
      hasEnrichingCompaniesRef.current = false;
      backendSupportsIsRevealingRef.current = false;
      pollingAttemptsRef.current = 0;
      lastPollingResponseRef.current = null;
      pollingPageRef.current = null;
      pollingFiltersRef.current = null;
      processedCombinationsRef.current.clear();
      isStartingPollingRef.current = false;
    }
  }, [
    activeSubTab,
    isPollingEnabled,
    setIsPollingEnabled,
    setEnrichingCompanies,
  ]);

  // Cancel polling when page or filters change
  useEffect(() => {
    if (
      isPollingEnabled &&
      pollingPageRef.current !== null &&
      pollingFiltersRef.current !== null
    ) {
      const filtersPayload = handleGeneratePayload();
      const isSaved = activeSubTab === LeadFinderSecondaryTab.SAVED;
      const currentPage = isSaved
        ? Math.ceil(
            (savedApiParam?.start || 1) /
              (savedApiParam?.take || getV2SavedTabPageSize()),
          )
        : apiParam?.page ?? 1;

      const currentFiltersStr = JSON.stringify(filtersPayload);
      const isPageMatch = currentPage === pollingPageRef.current;
      const isFilterMatch = currentFiltersStr === pollingFiltersRef.current;

      // Cancel polling if page or filters changed
      if (!isPageMatch || !isFilterMatch) {
        setIsPollingEnabled(false);
        setEnrichingCompanies({});
        hasEnrichingCompaniesRef.current = false;
        backendSupportsIsRevealingRef.current = false;
        pollingAttemptsRef.current = 0;
        lastPollingResponseRef.current = null;
        pollingPageRef.current = null;
        pollingFiltersRef.current = null;
        processedCombinationsRef.current.clear();
        isStartingPollingRef.current = false;
      }
    }
  }, [
    isPollingEnabled,
    apiParam,
    savedApiParam,
    activeSubTab,
    handleGeneratePayload,
    setIsPollingEnabled,
    setEnrichingCompanies,
  ]);

  const handleFetchCompaniesAfterReveal = useCallback(
    async (enablePolling = false, newPolling = false) => {
      try {
        if (newPolling) {
          const filtersPayload = handleGeneratePayload();
          const isSaved = activeSubTab === LeadFinderSecondaryTab.SAVED;
          const paginationPayload = isSaved ? savedApiParam : apiParam;
          const payload = {
            ...paginationPayload,
            ...filtersPayload,
          };

          // Store the polling context when starting a new poll request
          const pollingPage = isSaved
            ? Math.ceil(
                (savedApiParam?.start || 1) /
                  (savedApiParam?.take || getV2SavedTabPageSize()),
              )
            : apiParam?.page ?? 1;

          pollingPageRef.current = pollingPage;
          pollingFiltersRef.current = JSON.stringify(filtersPayload);

          // Call appropriate API based on active sub-tab
          let res;
          if (isSaved) {
            res = await getSavedCompanies(payload);
          } else {
            const params = new URLSearchParams();
            // params.set('tab', activeSubTab || 'total');
            params.set('_nr', '1');
            res = await getCompaniesV2(params.toString(), payload);
          }

          // Store the last response to use when polling stops
          lastPollingResponseRef.current = res;

          // Extract profiles from response
          const resolvedProfiles = Array.isArray(res?.payload)
            ? res?.payload
            : res?.payload?.profiles || [];

          // Detect whether backend actually sends `isRevealing` field
          // If backend doesn't send it, we won't start interval polling because we can't reliably know when to stop.
          const supportsIsRevealing = resolvedProfiles.some((company) =>
            Object.prototype.hasOwnProperty.call(company, 'isRevealing'),
          );
          if (supportsIsRevealing) {
            backendSupportsIsRevealingRef.current = true;
          }

          if (resolvedProfiles?.length) {
            // Check if there are any enriching companies in the current response
            const hasEnrichingCompanies = resolvedProfiles.some(
              (company) => company.isRevealing === true,
            );
            hasEnrichingCompaniesRef.current = hasEnrichingCompanies;

            // VALIDATION: Check if the response matches current page/filters before updating
            const currentFiltersPayload = handleGeneratePayload();
            const validationCurrentPage = isSaved
              ? Math.ceil(
                  (savedApiParam?.start || 1) /
                    (savedApiParam?.take || getV2SavedTabPageSize()),
                )
              : apiParam?.page ?? 1;

            const currentFiltersStr = JSON.stringify(currentFiltersPayload);
            const isPageMatch =
              validationCurrentPage === pollingPageRef.current;
            const isFilterMatch =
              currentFiltersStr === pollingFiltersRef.current;

            // Only update companies data if we're still on the same page and filters
            if (!isPageMatch || !isFilterMatch) {
              return;
            }

            // Update enriching companies state based on isRevealing status from response
            const enrichingCompaniesList = resolvedProfiles.filter(
              (company) => company.isRevealing === true,
            );
            const enrichedCompanies = resolvedProfiles.filter(
              (company) =>
                (company.isRevealed === true &&
                  company?.isRevealing === false) ||
                (company.isRevealed === true && !company.isRevealing),
            );

            // Update enriching companies state
            const updateEnrichingCompanies: [number, boolean][] = [];
            updateEnrichingCompanies.push(
              ...enrichingCompaniesList.map((company) => [
                company.id || company.company_id,
                true,
              ]),
            );
            updateEnrichingCompanies.push(
              ...enrichedCompanies.map((company) => [
                company.id || company.company_id,
                false,
              ]),
            );
            setEnrichingCompanies((prev) => {
              const updated = {
                ...prev,
                ...Object.fromEntries(updateEnrichingCompanies),
              };
              resolvedProfiles.forEach((company: any) => {
                const id = Number(company.id || company.company_id || 0);
                if (prev[id] === true && company.isRevealing === false) {
                  updated[id] = false;
                }
              });
              return updated;
            });

            // Update companiesData during polling to reflect latest isRevealing/isRevealed status
            if (isSaved) {
              const savedPayload = res?.payload || {};
              const savedPaginationSource = Array.isArray(savedPayload)
                ? null
                : savedPayload?.pagination;

              const takeValue =
                savedPaginationSource?.take ||
                savedApiParam?.take ||
                getV2SavedTabPageSize();
              const startValue =
                savedPaginationSource?.start || savedApiParam?.start || 1;
              const pageNumber = Math.ceil(startValue / takeValue);

              const savedPagination = savedPaginationSource
                ? {
                    page: pageNumber,
                    limit: takeValue,
                    totalRows: savedPaginationSource.total || 0,
                  }
                : {
                    page: pageNumber,
                    limit: takeValue,
                    totalRows: resolvedProfiles.length,
                  };

              setCompaniesData({
                profiles: resolvedProfiles.map((company: any) => ({
                  ...company,
                })),
                pagination: savedPagination,
              });
            } else {
              const totalRows =
                companiesDataRef.current?.pagination?.totalRows || 0;

              setCompaniesData({
                profiles: resolvedProfiles.map((company: any) => ({
                  ...company,
                })),
                pagination: {
                  page: validationCurrentPage,
                  limit: 25,
                  totalRows,
                },
              });
            }

            if (
              backendSupportsIsRevealingRef.current &&
              hasEnrichingCompaniesRef.current === false
            ) {
              stopSearchPolling(true);
              return;
            }
          } else {
            hasEnrichingCompaniesRef.current = false;
          }
        }

        // Enable polling if requested
        if (enablePolling) {
          if (backendSupportsIsRevealingRef.current) {
            if (hasEnrichingCompaniesRef.current) {
              setIsPollingEnabled(true);
            } else {
              setIsPollingEnabled(false);
              setEnrichingCompanies({});
              hasEnrichingCompaniesRef.current = false;
            }
          } else {
            // Backend doesn't provide isRevealing; stop here to avoid repeated polling calls.
            setIsPollingEnabled(false);
            setEnrichingCompanies({});
            hasEnrichingCompaniesRef.current = false;
          }
        }
      } catch (e: any) {
        if (e?.type === ExceptionTypes.Auth) {
          stopSearchPolling(false);
          return;
        }
        stopSearchPolling(false);
        toaster.error(e?.message || SystemErrors.InternalServerError);
      }
    },
    [
      handleGeneratePayload,
      activeSubTab,
      apiParam,
      savedApiParam,
      setCompaniesData,
      setEnrichingCompanies,
      setIsPollingEnabled,
      stopSearchPolling,
    ],
  );

  // Auto-start polling when search completes and companiesData contains any enriching companies
  useEffect(() => {
    const searchJustCompleted =
      previousIsLoadingRef.current === true && isLoading === false;
    previousIsLoadingRef.current = isLoading;

    if (!searchJustCompleted || isLoading) {
      return;
    }

    const profiles = companiesData?.profiles || [];
    if (profiles.length === 0) {
      return;
    }

    const filtersPayload = handleGeneratePayload();
    const isSaved = activeSubTab === LeadFinderSecondaryTab.SAVED;
    const currentPage = isSaved
      ? Math.ceil(
          (savedApiParam?.start || 1) /
            (savedApiParam?.take || getV2SavedTabPageSize()),
        )
      : apiParam?.page ?? 1;

    const currentFiltersStr = JSON.stringify(filtersPayload);
    const combinationKey = `${currentPage}:${currentFiltersStr}:${activeSubTab}`;

    if (processedCombinationsRef.current.has(combinationKey)) {
      return;
    }

    processedCombinationsRef.current.add(combinationKey);

    const hasEnrichingCompanies = profiles.some(
      (company) => company.isRevealing === true,
    );

    if (hasEnrichingCompanies) {
      hasEnrichingCompaniesRef.current = true;

      const isPageMatch = pollingPageRef.current === currentPage;
      const isFilterMatch = pollingFiltersRef.current === currentFiltersStr;

      if (
        (!isPollingEnabled || !isPageMatch || !isFilterMatch) &&
        !isStartingPollingRef.current
      ) {
        isStartingPollingRef.current = true;
        pollingAttemptsRef.current = 0;
        setIsFirstPollRequest(true);

        handleFetchCompaniesAfterReveal(true, true).catch(() => {
          isStartingPollingRef.current = false;
        });
      }
      return;
    }

    if (!isPollingEnabled) {
      hasEnrichingCompaniesRef.current = false;
    }
  }, [
    isLoading,
    companiesData,
    isPollingEnabled,
    apiParam,
    savedApiParam,
    activeSubTab,
    handleGeneratePayload,
    handleFetchCompaniesAfterReveal,
  ]);

  // Reset starting polling flag when polling actually starts
  useEffect(() => {
    if (isPollingEnabled) {
      isStartingPollingRef.current = false;
    }
  }, [isPollingEnabled]);

  useEffect(() => {
    if (shouldLogoutUser && isPollingEnabled) {
      stopSearchPolling(false);
    }
  }, [shouldLogoutUser, isPollingEnabled, stopSearchPolling]);

  // Polling effect
  useEffect(() => {
    const startPolling = () => {
      if (timerIdRef.current) {
        return;
      }

      pollingAttemptsRef.current = 0;

      timerIdRef.current = setInterval(async () => {
        pollingAttemptsRef.current += 1;

        // Stop polling as soon as backend indicates there are no revealing companies.
        // If the first polling response has all `isRevealing: false`, we should not retry.
        const shouldContinuePolling = hasEnrichingCompaniesRef.current;

        if (
          !shouldContinuePolling ||
          pollingAttemptsRef.current >= MAX_POLLING_LIMIT
        ) {
          setIsPollingEnabled(false);
          setEnrichingCompanies({});
          hasEnrichingCompaniesRef.current = false;
          pollingAttemptsRef.current = 0;

          // Update companiesData directly from last polling response
          if (lastPollingResponseRef.current) {
            const res = lastPollingResponseRef.current;
            const isSaved = activeSubTab === LeadFinderSecondaryTab.SAVED;

            const currentFiltersPayload = handleGeneratePayload();
            const currentPage = isSaved
              ? Math.ceil(
                  (savedApiParam?.start || 1) /
                    (savedApiParam?.take || getV2SavedTabPageSize()),
                )
              : apiParam?.page ?? 1;

            const currentFiltersStr = JSON.stringify(currentFiltersPayload);
            const isPageMatch = currentPage === pollingPageRef.current;
            const isFilterMatch =
              currentFiltersStr === pollingFiltersRef.current;

            const resolvedProfiles = Array.isArray(res?.payload)
              ? res?.payload
              : res?.payload?.profiles || [];

            if (isPageMatch && isFilterMatch && isSaved) {
              const savedPayload = res?.payload || {};
              const savedPaginationSource = Array.isArray(savedPayload)
                ? null
                : savedPayload?.pagination;

              const takeValue =
                savedPaginationSource?.take ||
                savedApiParam?.take ||
                getV2SavedTabPageSize();
              const startValue =
                savedPaginationSource?.start || savedApiParam?.start || 1;
              const pageNumber = Math.ceil(startValue / takeValue);

              const savedPagination = savedPaginationSource
                ? {
                    page: pageNumber,
                    limit: takeValue,
                    totalRows: savedPaginationSource.total || 0,
                  }
                : {
                    page: pageNumber,
                    limit: takeValue,
                    totalRows: resolvedProfiles.length,
                  };

              setCompaniesData({
                profiles: resolvedProfiles.map((company: any) => ({
                  ...company,
                })),
                pagination: savedPagination,
              });
            } else if (isPageMatch && isFilterMatch) {
              const totalRows =
                companiesDataRef.current?.pagination?.totalRows || 0;

              setCompaniesData({
                profiles: resolvedProfiles.map((company: any) => ({
                  ...company,
                })),
                pagination: {
                  page: currentPage,
                  limit: 25,
                  totalRows,
                },
              });
            }

            if (isPageMatch && isFilterMatch) {
              dispatch(getPostLoadMetaRequest());
            }

            pollingPageRef.current = null;
            pollingFiltersRef.current = null;
            lastPollingResponseRef.current = null;
          }
        } else {
          await handleFetchCompaniesAfterReveal(false, true);

          if (isFirstPollRequest) {
            setIsFirstPollRequest(false);
          }
        }
      }, BULK_ACTION_TIMEOUT);
    };

    const stopPolling = () => {
      setIsFirstPollRequest(true);
      pollingAttemptsRef.current = 0;
      if (timerIdRef.current) {
        clearInterval(timerIdRef.current);
        timerIdRef.current = null;
      }
    };

    if (isPollingEnabled) {
      if (!timerIdRef.current) {
        startPolling();
      }
    } else {
      stopPolling();
    }

    return () => {
      if (!isPollingEnabled) {
        stopPolling();
      }
    };
  }, [
    isPollingEnabled,
    isFirstPollRequest,
    activeSubTab,
    savedApiParam,
    apiParam,
    handleFetchCompaniesAfterReveal,
    setCompaniesData,
    setEnrichingCompanies,
    setIsPollingEnabled,
    handleGeneratePayload,
    dispatch,
  ]);

  // Single company reveal
  const handleRevealCompany = useCallback(
    async (companyId: number) => {
      try {
        // Set enriching state immediately
        setEnrichingCompanies((prev) => ({
          ...prev,
          [companyId]: true,
        }));

        const payload = {
          shouldEnrich: true,
          totalCompanies: 1,
          companyIds: [companyId],
        };

        const revealRes = await revealCompaniesAPI(payload);
        if (revealRes) {
          // Handle different response structures
          const payloadData = revealRes.payload || {};
          const message =
            payloadData.message ||
            revealRes.message ||
            payloadData.successMessage;
          const status = payloadData?.status;
          const success = payloadData.success !== false && status !== 0;
          const data = payloadData?.data;
          const notificationUuId = payloadData?.notificationUuId;
          const shouldPoll = Boolean(notificationUuId);

          if (!success || status === 0) {
            // Error case
            toaster.error(
              isInsufficientCreditsError(message)
                ? INSUFFICIENT_CREDITS_ERROR_MESSAGE
                : message || SystemErrors.InternalServerError,
              {
                theme: Theme.New,
              },
            );
            setEnrichingCompanies((prev) => {
              const updated = { ...prev };
              delete updated[companyId];
              return updated;
            });
          } else if (status === 2) {
            // Warning case - another action in progress
            toaster.warning(
              message ||
                'Your another bulk company reveal action is in progress. Please wait until it finishes',
              { theme: Theme.New },
            );
            setEnrichingCompanies((prev) => {
              const updated = { ...prev };
              delete updated[companyId];
              return updated;
            });
          } else {
            // Success case
            if (shouldPoll) {
              dispatch(enableBulkActionRequestPolling());
            }

            // Show success message if available
            if (message) {
              toaster.success(message, {
                theme: Theme.New,
              });
            }

            // Apply immediate data (if present) without using it to decide polling.
            const dataArray = Array.isArray(data) ? data : [];
            setCompaniesData((prev) => {
              const updatedProfiles = prev.profiles.map((company) => {
                const updatedCompany = dataArray.find(
                  (c: any) =>
                    (c.id || c.company_id) ===
                    (company.id || company.company_id),
                );
                return updatedCompany || company;
              });
              return {
                ...prev,
                profiles: updatedProfiles,
              };
            });

            // Start search polling ONLY if backend provided notificationUuId
            if (shouldPoll) {
              setTimeout(() => {
                handleFetchCompaniesAfterReveal(true, true);
              }, 0);
            } else {
              // No notificationUuId => do not start search polling
              setEnrichingCompanies((prev) => {
                const updated = { ...prev };
                delete updated[companyId];
                return updated;
              });
            }
          }
        } else {
          setEnrichingCompanies((prev) => {
            const updated = { ...prev };
            delete updated[companyId];
            return updated;
          });
        }
      } catch (e: any) {
        setEnrichingCompanies((prev) => {
          const updated = { ...prev };
          delete updated[companyId];
          return updated;
        });
        const backendMessage = e?.message || e?.response?.data?.message;
        toaster.error(
          isInsufficientCreditsError(backendMessage)
            ? INSUFFICIENT_CREDITS_ERROR_MESSAGE
            : backendMessage || SystemErrors.InternalServerError,
          {
            theme: Theme.New,
          },
        );
      }
    },
    [
      handleFetchCompaniesAfterReveal,
      setEnrichingCompanies,
      setCompaniesData,
      dispatch,
    ],
  );

  // Function to handle bulk reveal response and start polling if needed
  const handleBulkRevealResponse = useCallback(
    (revealResponse: any, companyIds: number[]) => {
      if (!revealResponse) {
        return;
      }

      const payloadData = revealResponse.payload || {};
      const status = payloadData?.status;
      const success = payloadData.success !== false && status !== 0;
      const data = payloadData?.data;
      const { notificationUuId } = payloadData;
      const shouldPoll = Boolean(notificationUuId);

      if (!success || status === 0) {
        // Error case - clear enriching state
        setEnrichingCompanies((prev) => {
          const updated = { ...prev };
          companyIds.forEach((id) => {
            delete updated[id];
          });
          return updated;
        });
        return;
      }

      if (status === 2) {
        // Warning case - clear enriching state
        setEnrichingCompanies((prev) => {
          const updated = { ...prev };
          companyIds.forEach((id) => {
            delete updated[id];
          });
          return updated;
        });
        return;
      }

      // Success case
      if (shouldPoll) {
        dispatch(enableBulkActionRequestPolling());
      }

      // Apply immediate data (if present) without using it to decide polling.
      const dataArray = Array.isArray(data) ? data : [];
      setCompaniesData((prev) => {
        const updatedProfiles = prev.profiles.map((company) => {
          const updatedCompany = dataArray.find(
            (c: any) =>
              (c.id || c.company_id) === (company.id || company.company_id),
          );
          return updatedCompany || company;
        });
        return {
          ...prev,
          profiles: updatedProfiles,
        };
      });

      // Start search polling ONLY if backend provided notificationUuId
      if (shouldPoll) {
        // Mark enriching state for the companies on current page/selection
        setEnrichingCompanies((prev) => {
          const updated = { ...prev };
          companyIds.forEach((id) => {
            updated[id] = true;
          });
          return updated;
        });

        setTimeout(() => {
          handleFetchCompaniesAfterReveal(true, true);
        }, 0);
      } else {
        // No notificationUuId => do not start search polling
        setEnrichingCompanies((prev) => {
          const updated = { ...prev };
          companyIds.forEach((id) => {
            delete updated[id];
          });
          return updated;
        });
      }
    },
    [
      setEnrichingCompanies,
      setCompaniesData,
      handleFetchCompaniesAfterReveal,
      dispatch,
    ],
  );

  return {
    handleRevealCompany,
    handleBulkRevealResponse,
    isPollingEnabled,
    enrichingCompanies,
  };
};
